/**
 * The account screen (T4.8) — change your own password.
 *
 * This is the route the admin reset points at: an administrator cannot reset
 * their OWN password through the admin screen, because that would let a hijacked
 * session set a new password without knowing the old one. Here the current
 * password is required, which is exactly the control that stops it.
 *
 * It is also where a user lands when an administrator has set their password
 * (`mustChangePassword`): every other screen redirects here until it is changed.
 *
 * A wrong current password comes back as 401 from the API with the sign-in
 * wording ("Incorrect email address or password"). That is not a lost session
 * and not about an email address, so it is replaced with a sentence that fits.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { useTerms } from '../lib/terms';
import { useDocumentTitle } from '../lib/documentTitle';
import { ApiError } from '../lib/api';
import { detailList, errorText, useChangePassword } from '../lib/admin';
import { FormError, ProblemList } from '../features/admin/ui';

export function AccountRoute() {
  const terms = useTerms();
  const { user } = useAuth();
  const navigate = useNavigate();
  const change = useChangePassword();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  // Captured on first render: the flag clears the moment the change succeeds,
  // and the screen must still know it was a forced change to say where to go.
  const [wasForced] = useState(Boolean(user?.mustChangePassword));

  useDocumentTitle('Account', terms.plural('blueprint'));

  const mismatch = confirm.length > 0 && next !== confirm;
  const ready = current.length > 0 && next.length > 0 && next === confirm && !change.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setDone(false);
    change.mutate(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => {
          setCurrent('');
          setNext('');
          setConfirm('');
          setDone(true);
          if (wasForced) navigate('/blueprints', { replace: true });
        },
      },
    );
  };

  const wrongCurrent = change.error instanceof ApiError && change.error.status === 401;
  const problems = detailList(change.error, 'problems');

  return (
    <div className="sheet login-sheet adm-sheet">
      <div className="masthead">
        <div>
          <h1>Account</h1>
          <p className="meta">
            {user ? `${user.name} · ${user.email}` : null}
          </p>
        </div>
      </div>

      {wasForced ? (
        <div className="adm-callout" role="note">
          <p>
            An administrator set your password. Choose a new one to continue; nothing else is
            available until you do.
          </p>
        </div>
      ) : null}

      <form className="login-form" onSubmit={submit} noValidate>
        <h2 className="adm-h3">Change your password</h2>
        <label className="field">
          <span className="field-label">Current password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span className="field-label">New password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
          />
          <span className="adm-help">
            At least 12 characters, not a common password, and different from the current one.
            Changing it signs you out of every other device.
          </span>
        </label>
        <label className="field">
          <span className="field-label">Repeat the new password</span>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            aria-invalid={mismatch ? true : undefined}
            required
          />
          {mismatch ? (
            <span className="adm-field-err" role="alert">
              The two new passwords do not match.
            </span>
          ) : null}
        </label>

        {problems.length > 0 ? (
          <ProblemList problems={problems} />
        ) : change.isError ? (
          <FormError>
            {wrongCurrent ? 'Your current password is not correct.' : errorText(change.error)}
          </FormError>
        ) : null}
        {done && !wasForced ? (
          <p className="status adm-notice" role="status">
            Your password was changed. Other devices have been signed out.
          </p>
        ) : null}

        <button className="btn primary" type="submit" disabled={!ready}>
          {change.isPending ? 'Changing…' : 'Change password'}
        </button>
      </form>
    </div>
  );
}
